import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ArrowRight,
  ArrowUpRight,
  Sparkle,
  Lightning,
  MagnifyingGlass,
  ListChecks,
  Rocket,
  EnvelopeSimple,
  ChatCircleDots,
  CurrencyDollar,
  Moon,
  Brain,
  ClockCountdown,
  Lock,
  Eye,
  HandPalm,
  ShieldCheck,
  Key,
  Robot,
  CheckCircle,
  CaretDown,
} from '@phosphor-icons/react';
import { useState } from 'react';
import { WaitlistButton } from '../components/WaitlistButton';

/* =============================================================
   LifeAdmin Landing — AdElevate-matched theme
   — Georgia serif display, navy/blue theme (blue-600 #2563eb primary,
     slate-900 / blue-900 dark surfaces, blue-700 → blue-400 gradient text)
     matched to AdElevate's visual system.
   ============================================================= */

const SERIF = "Georgia, 'Times New Roman', serif";

const featureTiles = [
  {
    icon: Brain,
    tag: 'Daily',
    title: 'AI Briefing, every morning.',
    description:
      'A fresh, generated brief of the day — bills due, renewals incoming, meetings landing. With a unique cover image tuned to your local time of day.',
    span: 'md:col-span-7',
    accent: true,
  },
  {
    icon: EnvelopeSimple,
    tag: 'Gmail',
    title: 'Auto-Scan your inbox.',
    description:
      'Background scans extract bills, subscriptions and appointments. Configurable scan depth, read-only by default.',
    span: 'md:col-span-5',
  },
  {
    icon: ListChecks,
    tag: 'Inbox',
    title: 'Smart task inbox with dedup.',
    description:
      'AI merges duplicates, scores priority by urgency & amount, and separates Active tasks from Forecasted ones.',
    span: 'md:col-span-5',
  },
  {
    icon: Robot,
    tag: 'Assistant',
    title: 'Chat with an AI that knows your life admin.',
    description:
      'Ask anything about your inbox: "draft a reply to my landlord", "is this bill higher than last month", "what did Telstra email about".',
    span: 'md:col-span-7',
  },
  {
    icon: ClockCountdown,
    tag: 'Routines',
    title: 'Custom recurring routines.',
    description:
      '“Every Sunday, scan my inbox and draft replies to any unread invoice.” Runs on a schedule, reports back.',
    span: 'md:col-span-4',
  },
  {
    icon: ChatCircleDots,
    tag: 'WhatsApp',
    title: 'Reminders from chat.',
    description:
      'Optional WhatsApp link surfaces appointments & receipts straight from your conversations.',
    span: 'md:col-span-4',
  },
  {
    icon: Moon,
    tag: 'Peace of mind',
    title: 'Sleep better at night.',
    description:
      'Everything important is being watched — bills, deadlines, renewals. You can finally ignore your inbox without the guilt.',
    span: 'md:col-span-4',
  },
];

const howSteps = [
  {
    icon: Lightning,
    title: 'Connect',
    description:
      'Sign in with Google. Minimum-scope OAuth — Gmail read + compose, Calendar read. Nothing else touched.',
  },
  {
    icon: MagnifyingGlass,
    title: 'Scan',
    description:
      'AI pulls bills, renewals and appointments from your inbox on a schedule you control.',
  },
  {
    icon: ListChecks,
    title: 'Prioritize',
    description:
      'Every task is scored by urgency, amount and snooze count. Duplicates auto-merge.',
  },
  {
    icon: Rocket,
    title: 'Act',
    description:
      'Draft replies, cancel subscriptions, or hand a recurring chore to a Custom Agent.',
  },
];

const privacyPillars = [
  {
    icon: Lock,
    title: 'AES-256 field-level encryption',
    meta: 'AES-256-GCM',
    description:
      'Task titles, descriptions, provider names, Gmail tokens, chat messages and drafts are individually encrypted at rest with authenticated AES-256-GCM.',
  },
  {
    icon: Eye,
    title: 'Only the data we need',
    meta: 'parse → discard',
    description:
      'We don\'t mirror your inbox. We parse an email, extract provider, amount and due date, then discard the raw body. Nothing is stored for training.',
  },
  {
    icon: HandPalm,
    title: 'One-click revoke',
    meta: 'Connections → Disconnect',
    description:
      'Disconnect Gmail, Calendar or WhatsApp from the Connections page and every access token is deleted from our side immediately.',
  },
];

const faqs = [
  {
    q: 'What exactly do you store from my Gmail?',
    a: 'We never store your raw inbox. When we scan, we read email headers and body just long enough to extract structured fields — provider, amount, due date, category — then discard the raw content. Those structured fields are stored encrypted (AES-256-GCM) and tied only to your account.',
  },
  {
    q: 'Is my data encrypted?',
    a: 'Yes. Sensitive fields — task titles, descriptions, provider names, Gmail OAuth tokens, chat messages with the AI assistant, and email drafts — are encrypted at rest with authenticated AES-256-GCM on a per-field basis. Even a database-level leak would not expose your content.',
  },
  {
    q: 'Can I revoke access in one click?',
    a: 'Absolutely. Go to Connections → click Disconnect on Gmail, Calendar or WhatsApp. Your tokens are deleted from our database, and you can separately revoke LifeAdmin from your Google account any time at myaccount.google.com → Security → Third-party access.',
  },
  {
    q: 'Do you read my emails? Do humans see them?',
    a: 'No human at LifeAdmin reads your emails. Scans are performed by Claude and GPT-4 class models in-memory to extract tasks, and results are stored encrypted. We do not use your data to train models, and we do not share or sell it.',
  },
  {
    q: 'What OAuth scopes do you request?',
    a: 'The minimum needed: Gmail read (to extract tasks), Gmail compose/send (only when you explicitly ask the assistant to draft or send on your behalf), and Calendar read. No contacts, no Drive, no Photos, no Chrome history.',
  },
  {
    q: 'What happens if I delete my account?',
    a: 'All your tasks, chat history, drafts, OAuth tokens and cached AI artefacts are purged from our database. Backups rotate out within 30 days. You can also export your data before deleting.',
  },
  {
    q: 'Where is my data hosted?',
    a: 'Your data is stored in an encrypted MongoDB instance managed by Emergent on secure cloud infrastructure. All traffic is served over HTTPS. The WhatsApp connector runs in an isolated Node.js process that only your session can talk to.',
  },
  {
    q: 'Does LifeAdmin train AI models on my data?',
    a: 'Never. We call provider APIs (Anthropic Claude, OpenAI) with standard business terms that exclude training on customer content. Your emails, tasks and chats are not used to improve any model.',
  },
];

/* ---------- Small composable bits ---------- */

const SectionEyebrow = ({ index, children, tone = 'slate' }) => {
  const color = tone === 'light' ? 'text-sky-300' : 'text-blue-600';
  const line = tone === 'light' ? 'bg-white/20' : 'bg-slate-200';
  const idxColor = tone === 'light' ? 'text-white/50' : 'text-slate-500';
  return (
    <div className="flex items-center gap-3 mb-6">
      <span
        className={`text-[11px] tracking-[0.25em] uppercase ${idxColor} font-medium`}
      >
        {index}
      </span>
      <span className={`h-px w-10 ${line}`} />
      <span
        className={`text-[11px] tracking-[0.25em] uppercase font-semibold ${color}`}
      >
        {children}
      </span>
    </div>
  );
};

/* =============================================================
   Landing
   ============================================================= */

export default function Landing() {
  const { user } = useAuth();
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div className="min-h-screen bg-white text-slate-900 selection:bg-sky-200 selection:text-slate-900">
      {/* --------- NAV (transparent over hero, matching AdElevate) --------- */}
      <header
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-slate-900/40 border-b border-white/5"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div
          className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-3 md:py-4 flex items-center justify-between gap-3"
          style={{
            paddingLeft: 'max(env(safe-area-inset-left), 1rem)',
            paddingRight: 'max(env(safe-area-inset-right), 1rem)',
          }}
        >
          <Link to="/" className="flex items-center" data-testid="landing-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_life-sync-23/artifacts/tcwyur1z_LifeAdminLogoBrain.png"
              alt="LifeAdmin AI"
              className="h-9 md:h-11 w-auto drop-shadow-[0_2px_8px_rgba(37,99,235,0.35)]"
            />
          </Link>
          <nav className="hidden md:flex items-center gap-10">
            {[
              ['Features', '#features'],
              ['How it works', '#how-it-works'],
              ['Privacy', '#security'],
              ['FAQ', '#faq'],
            ].map(([label, href]) => (
              <a
                key={label}
                href={href}
                className="text-[13px] text-white/80 hover:text-white transition-colors font-medium"
              >
                {label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            {user ? (
              <Link
                to="/dashboard"
                data-testid="go-to-dashboard"
                className="inline-flex items-center gap-1.5 rounded-full bg-white text-slate-900 px-4 sm:px-5 py-2 sm:py-2.5 text-[12px] sm:text-[13px] font-semibold hover:bg-slate-100 transition-all shadow-lg whitespace-nowrap"
              >
                Dashboard <ArrowRight size={14} weight="bold" />
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="login-link"
                  className="hidden sm:inline text-[13px] text-slate-200 hover:text-white transition-colors font-medium"
                >
                  Log in
                </Link>
                <WaitlistButton label="Join Waitlist" source="header" variant="header" testId="header-waitlist-cta" />
              </>
            )}
          </div>
        </div>
      </header>

      {/* --------- HERO (dark slate-900 with calm orbs, AdElevate style) --------- */}
      <section className="relative min-h-screen overflow-hidden bg-slate-900 flex items-center">
        {/* Gradient wash */}
        <div
          aria-hidden
          className="absolute inset-0 z-[1]"
          style={{
            background:
              'linear-gradient(135deg, rgba(15, 23, 42, 0.9) 0%, rgba(15, 23, 42, 0.4) 30%, rgba(59, 130, 246, 0.12) 50%, rgba(37, 99, 235, 0.12) 70%, rgba(15, 23, 42, 0.8) 100%)',
          }}
        />
        {/* Floating orbs */}
        <div
          aria-hidden
          className="absolute inset-0 z-[2] overflow-hidden pointer-events-none"
        >
          <div
            className="absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full opacity-40"
            style={{
              background:
                'radial-gradient(circle, rgba(59, 130, 246, 0.22) 0%, transparent 70%)',
              animation: 'calmFloat 20s ease-in-out infinite',
            }}
          />
          <div
            className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full opacity-35"
            style={{
              background:
                'radial-gradient(circle, rgba(14, 165, 233, 0.22) 0%, transparent 70%)',
              animation: 'calmFloat 15s ease-in-out infinite reverse',
            }}
          />
          <div
            className="absolute top-1/2 right-1/3 w-[400px] h-[400px] rounded-full opacity-20"
            style={{
              background:
                'radial-gradient(circle, rgba(37, 99, 235, 0.25) 0%, transparent 70%)',
              animation: 'calmFloat 25s ease-in-out infinite',
            }}
          />
        </div>
        {/* Faint grid */}
        <div
          aria-hidden
          className="absolute inset-0 z-[3] pointer-events-none opacity-[0.04]"
          style={{
            backgroundImage:
              'linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)',
            backgroundSize: '80px 80px',
          }}
        />

        <style>{`
          @keyframes calmFloat {
            0%, 100% { transform: translate(0, 0) scale(1); }
            50% { transform: translate(30px, -40px) scale(1.05); }
          }
        `}</style>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-28 pb-20 md:pt-32 md:pb-28 w-full">
          <div className="grid lg:grid-cols-12 gap-14 lg:gap-10 items-center">
            {/* --- Left: editorial headline --- */}
            <div className="lg:col-span-7 space-y-8">
              <div className="inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/5 backdrop-blur-md px-4 py-2">
                <Sparkle size={12} weight="fill" className="text-blue-400" />
                <span className="text-[11px] tracking-[0.22em] uppercase text-sky-300 font-semibold">
                  Your AI Chief of Staff
                </span>
              </div>
              <h1
                className="text-[clamp(2.5rem,6.5vw,5.25rem)] leading-[1.05] tracking-tight font-light text-slate-50"
                style={{
                  fontFamily: SERIF,
                  textShadow: '0 2px 20px rgba(0,0,0,0.5), 0 4px 40px rgba(0,0,0,0.3)',
                }}
              >
                Stop Managing Life.
                <br />
                <span
                  style={{
                    background:
                      'linear-gradient(135deg, rgb(29, 78, 216) 0%, rgb(37, 99, 235) 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    filter: 'drop-shadow(0 2px 10px rgba(37, 99, 235, 0.4))',
                  }}
                >
                  Start Living It.
                </span>
              </h1>
              <p className="text-[17px] md:text-[19px] leading-[1.6] text-slate-300 max-w-xl">
                LifeAdmin AI reads the bills, renewals and appointments piling
                up in your inbox, scores what actually matters, and hands you
                one calm daily brief — with encrypted storage and a one-click
                revoke.
              </p>

              <div className="flex flex-wrap items-center gap-3 pt-2">
                <WaitlistButton
                  label="Join the Waitlist"
                  source="hero"
                  variant="hero"
                  testId="hero-cta"
                />
                <a
                  href="#how-it-works"
                  data-testid="hero-watch-demo"
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 backdrop-blur-md text-white px-8 py-4 text-[15px] font-semibold hover:bg-white/10 transition-colors"
                >
                  See how it works
                  <ArrowUpRight size={16} weight="bold" />
                </a>
              </div>

              {/* Trust strip */}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3 pt-4">
                {[
                  { icon: Lock, label: 'AES-256-GCM encrypted' },
                  { icon: Key, label: 'Minimum-scope OAuth' },
                  { icon: HandPalm, label: 'One-click revoke' },
                ].map(({ icon: Icon, label }, i) => (
                  <div
                    key={i}
                    className="inline-flex items-center gap-2 rounded-full bg-white/5 backdrop-blur-sm border border-white/10 px-3 py-1.5"
                  >
                    <Icon size={14} weight="duotone" className="text-blue-400" />
                    <span className="text-[12px] text-slate-200">{label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* --- Right: editorial hero illustration --- */}
            <div className="lg:col-span-5">
              <div className="relative">
                {/* glow backdrop */}
                <div
                  aria-hidden
                  className="absolute -inset-8 rounded-[2rem] bg-gradient-to-br from-blue-500/25 via-sky-500/15 to-transparent blur-2xl"
                />
                <div className="relative rounded-[1.75rem] border border-white/15 bg-white/[0.04] backdrop-blur-xl overflow-hidden shadow-2xl">
                  <img
                    src="https://static.prod-images.emergentagent.com/jobs/4f12c38b-f1b0-4d59-b36f-105978941bf9/images/293a451bd21c58af7aefe8e7aed365668b0c153eb93ef45dd5619976b3bfeeb4.png"
                    alt="LifeAdmin AI — calm daily briefing"
                    className="w-full h-auto block"
                    loading="eager"
                  />
                </div>
                {/* Floating AI assistant badge */}
                <div className="absolute bottom-5 right-5 md:bottom-6 md:right-6 flex items-center gap-2.5 rounded-xl bg-slate-900/70 backdrop-blur-xl border border-white/15 px-3.5 py-2.5 shadow-lg">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-600 to-sky-500 flex items-center justify-center shadow-md shadow-blue-500/40">
                    <Robot size={14} className="text-white" weight="fill" />
                  </div>
                  <span className="text-[12px] text-slate-100">
                    AI drafted{' '}
                    <span className="text-sky-300 font-semibold">2 replies</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --------- FEATURES (Bento) --------- */}
      <section id="features" className="py-24 md:py-32 px-6 md:px-10 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16 md:mb-20 max-w-3xl">
            <SectionEyebrow index="01">Features</SectionEyebrow>
            <h2 className="text-4xl md:text-5xl leading-[1.08] tracking-tight text-slate-900 font-bold">
              Everything you forgot about,{' '}
              <span
                style={{
                  background:
                    'linear-gradient(135deg, rgb(29, 78, 216) 0%, rgb(37, 99, 235) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                in one tab.
              </span>
            </h2>
            <p className="mt-5 text-[17px] leading-relaxed text-slate-600">
              Seven quiet features that do the follow-ups, chasing and
              spreadsheet math you've been pretending to do for years.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6">
            {featureTiles.map((f, i) => (
              <article
                key={i}
                data-testid={`feature-card-${i}`}
                className={`group relative ${f.span ?? 'md:col-span-6'} rounded-3xl border p-7 md:p-9 transition-all duration-300 hover:-translate-y-1 ${
                  f.accent
                    ? 'bg-gradient-to-br from-blue-700 via-blue-800 to-slate-900 text-slate-50 border-blue-900/40 hover:shadow-[0_20px_60px_-20px_rgba(37,99,235,0.45)]'
                    : 'bg-white text-slate-900 border-slate-100 hover:border-sky-300 hover:shadow-[0_20px_40px_-24px_rgba(15,23,42,0.2)]'
                }`}
              >
                {f.accent && (
                  <div
                    aria-hidden
                    className="absolute inset-0 rounded-3xl opacity-30 pointer-events-none"
                    style={{
                      background:
                        'radial-gradient(circle at 80% 20%, rgba(96,165,250,0.3) 0%, transparent 50%)',
                    }}
                  />
                )}
                <div className="relative flex items-center justify-between mb-8">
                  <span
                    className={`text-[10px] tracking-[0.25em] uppercase font-semibold ${
                      f.accent ? 'text-sky-300' : 'text-blue-600'
                    }`}
                  >
                    {f.tag}
                  </span>
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      f.accent
                        ? 'bg-blue-500/20 text-sky-300'
                        : 'bg-blue-50 text-blue-600'
                    }`}
                  >
                    <f.icon size={20} weight="duotone" />
                  </div>
                </div>
                <h3
                  className={`relative text-[20px] md:text-[24px] leading-[1.25] tracking-tight mb-3 font-bold ${
                    f.accent ? 'text-white' : 'text-slate-900'
                  }`}
                >
                  {f.title}
                </h3>
                <p
                  className={`relative text-[14px] md:text-[15px] leading-relaxed max-w-xl ${
                    f.accent ? 'text-slate-300' : 'text-slate-600'
                  }`}
                >
                  {f.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* --------- HOW IT WORKS (dark gray-900, AdElevate-matched) --------- */}
      <section
        id="how-it-works"
        className="relative py-24 md:py-32 px-6 md:px-10 bg-gray-900 overflow-hidden"
      >
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-20 left-20 w-96 h-96 bg-blue-600 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-sky-500 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto">
          <div className="mb-16 max-w-3xl">
            <SectionEyebrow index="02" tone="light">How it works</SectionEyebrow>
            <h2 className="text-4xl md:text-5xl leading-[1.08] tracking-tight text-white font-bold">
              Four steps.{' '}
              <span
                style={{
                  background:
                    'linear-gradient(135deg, rgb(96, 165, 250) 0%, rgb(56, 189, 248) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                No spreadsheets.
              </span>
            </h2>
            <p className="mt-5 text-[17px] leading-relaxed text-slate-300">
              No zero-inbox guilt trips either.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-5 md:gap-6">
            {howSteps.map((s, i) => {
              const badgeGradients = [
                'from-blue-500 to-sky-500',
                'from-sky-500 to-cyan-500',
                'from-indigo-500 to-blue-600',
                'from-blue-600 to-sky-600',
              ];
              return (
                <div
                  key={i}
                  className="relative rounded-3xl bg-white/[0.04] backdrop-blur-md border border-white/10 p-7 md:p-8 hover:bg-white/[0.07] hover:border-sky-400/40 hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-[11px] tracking-[0.25em] uppercase text-slate-400 font-semibold">
                      0{i + 1}
                    </span>
                    <div
                      className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${badgeGradients[i]} flex items-center justify-center shadow-2xl shadow-blue-500/40`}
                    >
                      <s.icon size={22} weight="bold" className="text-white" />
                    </div>
                  </div>
                  <h3 className="text-[20px] tracking-tight mb-3 text-white leading-tight font-bold">
                    {s.title}
                  </h3>
                  <p className="text-[14px] leading-relaxed text-slate-400">
                    {s.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* --------- PRIVACY VAULT --------- */}
      <section
        id="security"
        className="relative py-28 md:py-36 px-6 md:px-10 bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 text-slate-50 overflow-hidden"
      >
        {/* Floating orbs */}
        <div aria-hidden className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-20 left-20 w-96 h-96 bg-blue-600 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-sky-500 rounded-full blur-3xl" />
        </div>
        {/* grid lines */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-[0.06]"
          style={{
            backgroundImage:
              'linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)',
            backgroundSize: '80px 80px',
          }}
        />
        <div className="max-w-6xl mx-auto relative">
          <div className="max-w-3xl">
            <SectionEyebrow index="03" tone="light">
              Privacy vault
            </SectionEyebrow>
            <h2 className="text-4xl md:text-5xl leading-[1.08] tracking-tight font-bold text-white">
              Your inbox{' '}
              <span
                style={{
                  background:
                    'linear-gradient(135deg, rgb(29, 78, 216) 0%, rgb(96, 165, 250) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                stays yours.
              </span>{' '}
              Encrypted end-to-end.
            </h2>
            <p className="mt-5 text-[16px] md:text-[17px] leading-relaxed text-slate-300 max-w-2xl">
              Sensitive fields are AES-256 encrypted before they hit the database — even we can't read them. We ask for the minimum Gmail and Calendar scopes, never sell your data, and you can cut us off in one click, any time.
            </p>
          </div>

          <div className="mt-14 md:mt-20 grid md:grid-cols-3 gap-5">
            {privacyPillars.map((p, i) => (
              <div
                key={i}
                data-testid={`privacy-pillar-${i}`}
                className="relative rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 p-8 md:p-9 hover:bg-white/[0.08] hover:border-blue-400/30 transition-all"
              >
                <div className="flex items-center justify-between mb-8">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-600 to-sky-500 flex items-center justify-center shadow-lg shadow-blue-500/30">
                    <p.icon size={20} weight="bold" className="text-white" />
                  </div>
                  <span className="text-[10px] tracking-[0.22em] uppercase text-sky-300/90 font-semibold">
                    {p.meta}
                  </span>
                </div>
                <h3 className="text-[20px] leading-[1.3] tracking-tight text-white mb-3 font-bold">
                  {p.title}
                </h3>
                <p className="text-[14px] leading-relaxed text-slate-300">
                  {p.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-14 flex flex-wrap items-center gap-4">
            <Link
              to="/privacy"
              data-testid="read-privacy-policy"
              className="inline-flex items-center gap-2 rounded-full bg-white text-slate-900 px-6 py-3 text-[13px] font-semibold hover:bg-blue-50 transition-colors"
            >
              Read the full privacy policy <ArrowUpRight size={14} weight="bold" />
            </Link>
            <span className="text-[11px] tracking-[0.2em] uppercase text-white/50 font-medium">
              GDPR-minded · No model training · No selling
            </span>
          </div>
        </div>
      </section>

      {/* --------- FAQ (AdElevate style) --------- */}
      <section
        id="faq"
        className="py-24 md:py-32 px-6 md:px-10 bg-gradient-to-br from-slate-50 to-white"
      >
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <SectionEyebrow index="04">
              <span className="block text-center">Data & Privacy FAQ</span>
            </SectionEyebrow>
            <h2 className="text-4xl md:text-5xl leading-[1.08] tracking-tight text-slate-900 font-bold">
              Answers to what you're{' '}
              <span
                style={{
                  background:
                    'linear-gradient(135deg, rgb(29, 78, 216) 0%, rgb(37, 99, 235) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                about to ask.
              </span>
            </h2>
            <p className="mt-5 text-[17px] leading-relaxed text-slate-600">
              We take privacy seriously enough to write about it plainly.
            </p>
          </div>

          <div className="space-y-3" data-testid="faq-accordion">
            {faqs.map((item, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div
                  key={idx}
                  className={`bg-white rounded-2xl border-2 transition-all duration-300 overflow-hidden shadow-sm hover:shadow-md ${
                    isOpen
                      ? 'border-blue-400'
                      : 'border-slate-100 hover:border-sky-300'
                  }`}
                  data-testid={`faq-item-${idx}`}
                >
                  <button
                    onClick={() => setOpenFaq(isOpen ? -1 : idx)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 rounded-2xl"
                    data-testid={`faq-trigger-${idx}`}
                    aria-expanded={isOpen}
                  >
                    <span className="flex items-start gap-4 flex-1 min-w-0">
                      <span className="text-[11px] tracking-[0.22em] uppercase text-slate-400 pt-1.5 font-semibold">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <span className="text-[17px] md:text-[18px] leading-[1.4] text-slate-900 font-semibold pr-4">
                        {item.q}
                      </span>
                    </span>
                    <span
                      className={`flex-shrink-0 ml-4 w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-sky-500 flex items-center justify-center shadow-md transition-transform duration-300 ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                    >
                      <CaretDown size={14} className="text-white" weight="bold" />
                    </span>
                  </button>
                  <div
                    className={`grid transition-all duration-300 ease-in-out ${
                      isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div
                        className="px-6 pb-6 pl-[calc(1.5rem+2.75rem)] text-[15px] leading-[1.7] text-slate-600"
                        data-testid={`faq-content-${idx}`}
                      >
                        {item.a}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-10 rounded-2xl bg-gradient-to-br from-blue-50 via-white to-blue-50 border border-sky-200/50 p-6 md:p-7 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-sky-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-500/30">
                <ShieldCheck size={18} weight="fill" className="text-white" />
              </div>
              <div>
                <p className="text-[14px] font-semibold text-slate-900">
                  Still have questions?
                </p>
                <p className="text-[13px] text-slate-600">
                  Read the full policy or reach out — we answer the hard ones too.
                </p>
              </div>
            </div>
            <Link
              to="/privacy"
              data-testid="faq-privacy-link"
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white px-5 py-2.5 text-[13px] font-semibold hover:bg-slate-800 transition-colors"
            >
              Privacy policy <ArrowUpRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* --------- CTA (AdElevate-style gradient banner) --------- */}
      <section className="relative py-28 md:py-32 px-6 md:px-10 bg-gradient-to-br from-blue-600 via-sky-600 to-blue-700 overflow-hidden">
        <div aria-hidden className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute top-20 left-20 w-96 h-96 bg-sky-300 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-400 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-md border border-white/20 px-4 py-1.5 mb-8">
            <Sparkle size={12} weight="fill" className="text-white" />
            <span className="text-[11px] tracking-[0.22em] uppercase text-white font-semibold">
              Free to start
            </span>
          </div>
          <h2
            className="text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-white font-light"
            style={{
              fontFamily: SERIF,
              textShadow: '0 2px 20px rgba(0,0,0,0.2)',
            }}
          >
            Offload your <span className="italic">mental load.</span>
          </h2>
          <p className="mt-6 text-[18px] md:text-[20px] leading-relaxed text-white/90 max-w-xl mx-auto">
            Nothing stored until you connect Gmail. Cancel in one click.
          </p>
          <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
            <WaitlistButton
              label="Join the Waitlist"
              source="footer"
              variant="primary"
              testId="footer-cta"
              className="!bg-white !text-blue-600 !px-9 !py-4 !text-[15px] !font-bold hover:scale-[1.04]"
            />
            <a
              href="#faq"
              data-testid="footer-see-faq"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 backdrop-blur-md text-white px-9 py-4 text-[15px] font-semibold hover:bg-white/15 transition-colors"
            >
              See FAQ first
              <ArrowUpRight size={16} weight="bold" />
            </a>
          </div>
          <div className="mt-8 flex justify-center items-center gap-6 text-white/90 text-sm flex-wrap">
            {['50 free scans', 'No credit card', 'Cancel anytime'].map((t, i) => (
              <span key={i} className="flex items-center gap-1.5">
                <CheckCircle size={16} weight="fill" className="text-sky-200" />
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* --------- FOOTER --------- */}
      <footer className="py-10 px-6 md:px-10 bg-gray-900 text-slate-300">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_life-sync-23/artifacts/tcwyur1z_LifeAdminLogoBrain.png"
              alt="LifeAdmin AI"
              className="h-9 w-auto"
            />
            <span className="text-[12px] text-slate-400 hidden sm:inline">
              · Designed for focus.
            </span>
          </div>
          <div className="flex items-center gap-8 text-[13px] text-slate-400">
            <Link
              to="/privacy"
              data-testid="footer-privacy"
              className="hover:text-white transition-colors"
            >
              Privacy
            </Link>
            <a
              href="#faq"
              data-testid="footer-faq"
              className="hover:text-white transition-colors"
            >
              FAQ
            </a>
            <a
              href="#security"
              data-testid="footer-security"
              className="hover:text-white transition-colors"
            >
              Security
            </a>
            <span className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-medium">
              © 2026
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
